import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  type Transformer,
} from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { LexicalComposer, type InitialConfigType } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import {
  DEFAULT_TRANSFORMERS,
  MarkdownShortcutPlugin,
} from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { useEffect, useState } from "react";

import { cn } from "~/lib/utils";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { MarkdownEditorToolbar } from "./MarkdownEditorToolbar";
import { hasUnsupportedVisualMarkdown } from "./markdownSupport";

const MARKDOWN_TRANSFORMERS: Transformer[] = [CHECK_LIST, ...DEFAULT_TRANSFORMERS];

const EDITOR_THEME: NonNullable<InitialConfigType["theme"]> = {
  code: "my-3 block overflow-x-auto rounded-md border border-border/60 bg-muted/30 p-3 font-mono text-sm",
  heading: {
    h1: "mb-2 mt-4 text-xl font-semibold tracking-tight",
    h2: "mb-2 mt-4 text-lg font-semibold tracking-tight",
    h3: "mb-1.5 mt-3 text-base font-semibold",
  },
  link: "font-medium text-primary underline decoration-primary/35 underline-offset-2",
  list: {
    checklist: "my-2 list-none space-y-1 pl-1",
    listitem: "ml-5 pl-0.5",
    listitemChecked: "relative ml-6 list-none text-muted-foreground line-through",
    listitemUnchecked: "relative ml-6 list-none",
    nested: { listitem: "list-none" },
    ol: "my-2 list-decimal space-y-1 pl-5",
    ul: "my-2 list-disc space-y-1 pl-5",
  },
  paragraph: "my-2 leading-6 first:mt-0 last:mb-0",
  quote: "my-3 border-l-2 border-border pl-3 text-muted-foreground",
  text: {
    bold: "font-semibold",
    code: "rounded bg-muted/55 px-1 py-0.5 font-mono text-[0.9em]",
    italic: "italic",
    strikethrough: "line-through",
  },
};

export interface MarkdownEditorProps {
  readonly value: string;
  readonly onChange: (markdown: string) => void;
  readonly ariaLabel: string;
  readonly placeholder: string;
  readonly autoFocus?: boolean;
  readonly className?: string;
  readonly maxLength?: number;
  /**
   * compact = quieter chrome for status-update composers (writing-first).
   * default = full mode switcher + full formatting toolbar.
   */
  readonly density?: "default" | "compact";
  readonly disabled?: boolean;
}

function ExternalMarkdownValuePlugin(props: { readonly value: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const currentValue = $convertToMarkdownString(MARKDOWN_TRANSFORMERS, undefined, true);
      if (currentValue === props.value) return;
      $convertFromMarkdownString(props.value, MARKDOWN_TRANSFORMERS, undefined, true);
    });
  }, [editor, props.value]);

  return null;
}

function MarkdownEditorEditabilityPlugin(props: { readonly disabled: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => editor.setEditable(!props.disabled), [editor, props.disabled]);
  return null;
}

function VisualMarkdownEditor(
  props: MarkdownEditorProps & { readonly toolbarVariant: "default" | "compact" },
) {
  const initialConfig: InitialConfigType = {
    namespace: "ForgeProjectMarkdown",
    nodes: [
      AutoLinkNode,
      CodeHighlightNode,
      CodeNode,
      HeadingNode,
      HorizontalRuleNode,
      LinkNode,
      ListItemNode,
      ListNode,
      QuoteNode,
    ],
    theme: EDITOR_THEME,
    editorState: () =>
      $convertFromMarkdownString(props.value, MARKDOWN_TRANSFORMERS, undefined, true),
    onError: (error) => {
      throw error;
    },
    editable: !props.disabled,
  };
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <MarkdownEditorEditabilityPlugin disabled={props.disabled ?? false} />
      <MarkdownEditorToolbar variant={props.toolbarVariant} disabled={props.disabled ?? false} />
      <div className="relative">
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              aria-label={props.ariaLabel}
              aria-disabled={props.disabled}
              className={cn(
                "resize-y overflow-auto px-3 py-2.5 text-sm text-foreground outline-none",
                props.density === "compact" ? "min-h-28" : "min-h-32",
              )}
            />
          }
          placeholder={
            <div className="pointer-events-none absolute left-3 top-2.5 text-sm text-muted-foreground/55">
              {props.placeholder}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <LinkPlugin />
        <MarkdownShortcutPlugin transformers={MARKDOWN_TRANSFORMERS} />
        <ExternalMarkdownValuePlugin value={props.value} />
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(editorState) => {
            if (props.disabled) return;
            editorState.read(() =>
              props.onChange($convertToMarkdownString(MARKDOWN_TRANSFORMERS, undefined, true)),
            );
          }}
        />
        {props.autoFocus ? <AutoFocusPlugin /> : null}
      </div>
    </LexicalComposer>
  );
}

function EditorModeToggle(props: {
  readonly mode: "visual" | "markdown";
  readonly unsupported: boolean;
  readonly compact: boolean;
  readonly onModeChange: (mode: "visual" | "markdown") => void;
  readonly disabled?: boolean;
}) {
  if (props.compact) {
    return (
      <div className="inline-flex items-center gap-1.5" aria-label="Editor mode">
        <button
          type="button"
          aria-pressed={props.mode === "visual"}
          onClick={() => props.onModeChange("visual")}
          disabled={props.unsupported || props.disabled}
          className={cn(
            "min-w-touch rounded-lg px-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 md:min-w-0 md:rounded md:px-1 md:py-0.5 md:text-xs",
            RESPONSIVE_TEXT_BUTTON_CLASS,
            props.mode === "visual"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Visual
        </button>
        <span className="text-muted-foreground/40" aria-hidden>
          /
        </span>
        <button
          type="button"
          aria-pressed={props.mode === "markdown"}
          onClick={() => props.onModeChange("markdown")}
          {...(props.disabled !== undefined ? { disabled: props.disabled } : {})}
          className={cn(
            "min-w-touch rounded-lg px-2 text-sm font-medium transition-colors md:min-w-0 md:rounded md:px-1 md:py-0.5 md:text-xs",
            RESPONSIVE_TEXT_BUTTON_CLASS,
            props.mode === "markdown"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Markdown
        </button>
      </div>
    );
  }

  return (
    <div
      className="inline-flex gap-[8px] rounded-lg bg-muted p-1 md:gap-0.5 md:rounded-md md:bg-muted/35 md:p-0.5"
      aria-label="Editor mode"
    >
      <button
        type="button"
        aria-pressed={props.mode === "visual"}
        onClick={() => props.onModeChange("visual")}
        disabled={props.unsupported || props.disabled}
        className={cn(
          "rounded-lg px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 md:rounded md:px-2 md:py-1 md:text-xs",
          RESPONSIVE_TEXT_BUTTON_CLASS,
          props.mode === "visual"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground",
        )}
      >
        Visual
      </button>
      <button
        type="button"
        aria-pressed={props.mode === "markdown"}
        onClick={() => props.onModeChange("markdown")}
        disabled={props.disabled}
        className={cn(
          "rounded-lg px-3 text-sm font-medium transition-colors md:rounded md:px-2 md:py-1 md:text-xs",
          RESPONSIVE_TEXT_BUTTON_CLASS,
          props.mode === "markdown"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground",
        )}
      >
        Markdown
      </button>
    </div>
  );
}

function shouldShowCharacterCount(
  valueLength: number,
  maxLength: number | undefined,
  compact: boolean,
  overLimit: boolean,
): maxLength is number {
  if (maxLength === undefined) return false;
  return overLimit || !compact || valueLength >= Math.floor(maxLength * 0.8);
}

function MarkdownSourceEditor(props: {
  readonly value: string;
  readonly ariaLabel: string;
  readonly placeholder: string;
  readonly autoFocus?: boolean;
  readonly compact: boolean;
  readonly onChange: (markdown: string) => void;
  readonly disabled?: boolean;
}) {
  return (
    <textarea
      aria-label={props.ariaLabel}
      disabled={props.disabled}
      value={props.value}
      onChange={(event) => props.onChange(event.currentTarget.value)}
      autoFocus={props.autoFocus}
      placeholder={props.placeholder}
      spellCheck
      className={cn(
        "w-full resize-y bg-background px-3 py-2.5 font-mono text-sm leading-6 text-foreground outline-none placeholder:text-muted-foreground/55",
        props.compact ? "min-h-28" : "min-h-40",
      )}
    />
  );
}

export function MarkdownEditor(props: MarkdownEditorProps) {
  const disabled = props.disabled ?? false;
  const unsupported = hasUnsupportedVisualMarkdown(props.value);
  const compact = props.density === "compact";
  const [mode, setMode] = useState<"visual" | "markdown">(() =>
    unsupported ? "markdown" : "visual",
  );
  const overLimit = props.maxLength !== undefined && props.value.length > props.maxLength;
  const showCount = shouldShowCharacterCount(
    props.value.length,
    props.maxLength,
    compact,
    overLimit,
  );
  const useVisual = mode === "visual" && !unsupported;

  return (
    <div
      data-markdown-editor
      className={cn(
        "overflow-hidden bg-background",
        compact
          ? "rounded-xl border border-border/40 focus-within:border-border/70 focus-within:ring-1 focus-within:ring-ring/30"
          : "rounded-md border border-border/60 focus-within:ring-2 focus-within:ring-ring",
        overLimit ? "border-destructive/70" : undefined,
        props.className,
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between",
          compact
            ? "border-b border-border/50 px-2.5 py-1"
            : "border-b border-border/60 bg-muted/10 px-2 py-1",
        )}
      >
        <EditorModeToggle
          mode={mode}
          unsupported={unsupported}
          compact={compact}
          onModeChange={setMode}
          disabled={disabled}
        />
        {showCount ? (
          <span
            className={cn(
              "text-xs tabular-nums",
              overLimit ? "text-destructive" : "text-muted-foreground/60",
            )}
          >
            {props.value.length.toLocaleString()} / {props.maxLength.toLocaleString()}
          </span>
        ) : null}
      </div>
      {unsupported ? (
        <p className="border-b border-border/50 bg-warning-surface px-3 py-2 text-xs text-warning-foreground">
          This content uses Markdown syntax that the visual editor cannot preserve. Edit it in
          Markdown mode to keep the source intact.
        </p>
      ) : null}
      {useVisual ? (
        <VisualMarkdownEditor {...props} toolbarVariant={compact ? "compact" : "default"} />
      ) : (
        <MarkdownSourceEditor
          value={props.value}
          ariaLabel={props.ariaLabel}
          placeholder={props.placeholder}
          {...(props.autoFocus !== undefined ? { autoFocus: props.autoFocus } : {})}
          compact={compact}
          onChange={props.onChange}
          disabled={disabled}
        />
      )}
    </div>
  );
}
